import { useState, useEffect, useRef } from "preact/hooks";

import { fetchFormEmbed, submitPhoneNumber, resendFormLink, FormVueApiError } from "../api.js";
import { validatePhoneNumber } from "../validation.js";
import { pickedAddressComponents, type AddressPick } from "../address-search.js";
import type { AddressVerificationRecordMap } from "../address-verification.js";
import { applyTheme } from "../theme.js";
import { isDesktop } from "../detect.js";
import {
  captureCallRail,
  collectCallRailContact,
  prepareCallRailCapture,
} from "../callrail.js";
import type { BaseConfig, FormEmbedConfig, FormEmbedField } from "../types.js";
import type { FormLinkResponse } from "../schema-types.js";

export type View = "loading" | "form" | "submitting" | "success" | "error" | "coupon" | "resending";

interface UseEmbedFormOptions {
  config: BaseConfig & { id: string };
  containerRef: { current: HTMLElement | null };
  onSuccessView?: () => void;
  onResendView?: () => void;
  /**
   * When "coupon", a coupon interstitial is shown between submit and success:
   * the form-link is created with `sendSMS: false` and the SMS is sent only
   * when the user confirms via `submitResend`. Default mode is unchanged.
   */
  mode?: "default" | "coupon";
  /**
   * When true AND the device is mobile, the form-link is created without an SMS
   * and the browser is redirected straight to the form (the same URL the SMS
   * would contain). Desktop is unaffected — it keeps the SMS flow.
   */
  redirectOnMobile?: boolean;
}

/** Mobile redirect never texts. Coupon mode texts on confirm, even with SMS off. */
export function resolveSmsSent(
  embedConfig: FormEmbedConfig | null,
  mode: { couponMode: boolean; shouldRedirect: boolean },
): boolean {
  if (mode.shouldRedirect) return false;
  if (mode.couponMode) return true;
  return embedConfig?.config?.sendSMS !== false;
}

export function useEmbedForm({ config, containerRef, onSuccessView, onResendView, mode, redirectOnMobile }: UseEmbedFormOptions) {
  const couponMode = mode === "coupon";
  // Resolve device once — `isDesktop()` reads the DOM and shouldn't re-run per render.
  const [isMobile] = useState(() => !isDesktop());
  const shouldRedirect = !!redirectOnMobile && isMobile;
  const [view, setView] = useState<View>("loading");
  const [embedConfig, setEmbedConfig] = useState<FormEmbedConfig | null>(null);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successName, setSuccessName] = useState("");
  const [formLinkId, setFormLinkId] = useState("");
  // Full form-link response, kept so the coupon-redirect path can fire `onSuccess`.
  const formLinkDataRef = useRef<FormLinkResponse | null>(null);

  function loadForm() {
    // Builder preview: use the injected config and skip the network fetch.
    if (config._formConfig) {
      setEmbedConfig(config._formConfig);
      if (containerRef.current) {
        applyTheme(containerRef.current, config._formConfig.customTheme);
      }
      setView("form");
      return;
    }

    if (!config.id) {
      setView("error");
      return;
    }

    setView("loading");

    fetchFormEmbed(config.embedApiUrl, config.id)
      .then((data) => {
        setEmbedConfig(data);

        if (containerRef.current) {
          applyTheme(containerRef.current, data.customTheme);
        }

        setView("form");
      })
      .catch(() => {
        setView("error");
      });
  }

  // Reload when the form changes or the builder pushes a new preview config.
  useEffect(() => { loadForm(); }, [config.id, config._formConfig]);

  /**
   * Mobile no-SMS flow: navigate straight to the form using the same URL the
   * SMS would have contained (`shareUrl` + the new form-link id as `tid`).
   */
  function redirectToForm(linkId: string) {
    try {
      const url = new URL(config.shareUrl);
      url.searchParams.set("tid", linkId);
      window.location.href = url.toString();
    } catch {
      // Fallback if shareUrl is somehow invalid — show success so the user isn't stuck.
      setSubmitting(false);
      onSuccessView?.();
      setView("success");
    }
  }

  function handleFormSubmit(
    values: Record<string, any>,
    fields: FormEmbedField[],
    addressPicks: ReadonlyMap<string, AddressPick> = new Map(),
    addressVerification?: AddressVerificationRecordMap,
  ) {
    if (submitting || !embedConfig) return;

    setSubmitError("");

    const trimmed: Record<string, any> = {};
    for (const [name, val] of Object.entries(values)) {
      trimmed[name] = typeof val === "string" ? val.trim() : val;
    }

    const phoneField = fields.find((f) => f.type === "phone");
    if (!phoneField) {
      setSubmitError("Form configuration error: no phone field found.");
      return;
    }

    const phoneValue = trimmed[phoneField.name];
    const phoneEmpty = !phoneValue;
    let phoneFormatted: string | null = null;

    if (phoneEmpty && phoneField.required) {
      setSubmitError("Phone number is required.");
      return;
    }

    if (!phoneEmpty) {
      const phoneValidation = validatePhoneNumber(phoneValue);
      if (!phoneValidation.isValid || !phoneValidation.formatted) {
        setSubmitError("Invalid phone number.");
        return;
      }
      phoneFormatted = phoneValidation.formatted;
    }

    const preData: Record<string, unknown> = { ...config.prefill };
    for (const field of fields) {
      const value = field.type === "phone" ? (phoneFormatted ?? "") : trimmed[field.name];
      // An optional field left blank submits "". It must not erase a prefill.
      const blank = value === "" || value === undefined;
      if (!blank || !(field.name in preData)) {
        preData[field.name] = value;
      }
    }
    // `fields` holds only the questions on the visitor's branch. A prefilled
    // answer to an embed question on another branch is not sent either.
    for (const screen of embedConfig.screens) {
      const name = screen.config.field.name;
      if (!fields.some((f) => f.name === name)) delete preData[name];
    }

    const addressComponents = pickedAddressComponents(fields, trimmed, addressPicks);
    if (addressComponents) preData._addressComponents = addressComponents;
    // The widget's per-field USPS verification record, same shape and key.
    if (addressVerification) preData._addressVerification = addressVerification;

    const nameField = fields.find((f) => /first.?name|name/i.test(f.name) && f.type === "text");
    const rawName = nameField ? String(trimmed[nameField.name] ?? "").split(/\s+/)[0] : "";
    setSuccessName(rawName);

    const callRailContact = collectCallRailContact(preData, fields, phoneFormatted);
    const callRailCapture = prepareCallRailCapture(
      config.attribution.callrail,
      callRailContact,
    );
    const payload = {
      formId: embedConfig.formId,
      entityId: phoneFormatted ?? "",
      phoneNumber: phoneFormatted ?? "",
      utmSource: config.attribution.utm.source,
      preData,
      embedId: config.id || undefined,
      campaignId: config.attribution.campaignId,
      campaignSlug: config.attribution.campaignSlug,
      utm: config.attribution.utm,
      metadata: {
        ...config.embed,
        ...config.attribution.metadata,
        ...(callRailCapture ? { callrail: callRailCapture.metadata } : {}),
      },
      ...(couponMode || shouldRedirect ? { sendSMS: false } : {}),
    };

    config.onSubmit?.(phoneFormatted ?? "");

    // Preview mode: hand the payload to the builder, skip the network, mirror
    // the real flow's next view (coupon interstitial or success).
    if (config.preview) {
      config.onPreviewSubmit?.(payload);
      if (couponMode) {
        setView("coupon");
      } else {
        onSuccessView?.();
        setView("success");
      }
      return;
    }

    setSubmitting(true);
    setView("submitting");

    submitPhoneNumber(config.embedApiUrl, payload)
      .then(async (data) => {
        const linkId = typeof data?.id === "string" ? data.id : "";
        setFormLinkId(linkId);
        formLinkDataRef.current = data;
        const callRailResult = callRailCapture
          ? captureCallRail(callRailCapture)
          : null;
        if (couponMode) {
          if (callRailResult) void callRailResult;
          setSubmitting(false);
          setView("coupon");
        } else if (shouldRedirect && linkId) {
          // Give CallRail only its short bounded capture window before navigation.
          config.onSuccess?.(data);
          if (callRailResult) await callRailResult;
          redirectToForm(linkId);
        } else {
          if (callRailResult) void callRailResult;
          onSuccessView?.();
          setView("success");
          config.onSuccess?.(data);
        }
      })
      .catch((err) => {
        setSubmitting(false);
        setView("form");

        const isKnownError =
          err instanceof FormVueApiError &&
          (err.status === 400 || err.status === 404 || err.status === 422 || err.status === 429);
        setSubmitError(
          isKnownError ? err.message : "Something went wrong. Please try again."
        );

        const error = err instanceof Error ? err : new Error("Unknown error");
        config.onError?.(error);
      });
  }

  function handleResend() {
    onResendView?.();
    setView("form");
    setSubmitting(false);
    setSubmitError("");
  }

  /**
   * Coupon-flow CTA: sends the SMS for the already-created form-link via the
   * resend endpoint. Distinct from `handleResend` (the success-view link).
   */
  function submitResend() {
    if (config.preview) {
      onSuccessView?.();
      setView("success");
      return;
    }

    if (!couponMode || !formLinkId || view === "resending") return;

    // Mobile: redirect to the form instead of sending the SMS. Fire `onSuccess`
    // first so host-app conversion tracking matches the other success paths.
    if (shouldRedirect) {
      setView("resending");
      if (formLinkDataRef.current) config.onSuccess?.(formLinkDataRef.current);
      redirectToForm(formLinkId);
      return;
    }

    setSubmitError("");
    setView("resending");

    resendFormLink(config.embedApiUrl, formLinkId)
      .then((data) => {
        onSuccessView?.();
        setView("success");
        config.onSuccess?.(data);
      })
      .catch((err) => {
        setView("coupon");

        const isKnownError =
          err instanceof FormVueApiError &&
          (err.status === 400 || err.status === 404 || err.status === 422 || err.status === 429);
        setSubmitError(
          isKnownError ? err.message : "Something went wrong. Please try again."
        );

        const error = err instanceof Error ? err : new Error("Unknown error");
        config.onError?.(error);
      });
  }

  return {
    view,
    embedConfig,
    addressSearchUrl: config.embedApiUrl,
    submitError,
    submitting,
    successName,
    formLinkId,
    willRedirect: shouldRedirect,
    smsSent: resolveSmsSent(embedConfig, { couponMode, shouldRedirect }),
    loadForm,
    handleFormSubmit,
    clearSubmitError: () => setSubmitError(""),
    handleResend,
    submitResend,
  };
}
